import { CategoryListingConfiguration } from '../../types/api'
import ListingDefaultResolver from '../products_v5/components/JoinedForm/ListingDefaultResolver'
import { EBAY_CHANNEL_ID, EbayForm } from './types'
import { findActiveCmc } from './channelHelpers'

interface Action extends Record<string, any> {
  type: string
}

const EMPTY_EBAY_FORM: EbayForm = {
  specifics: {},
  productListingDetails: {}
}

// ListingDefaultResolver assumes every mapping has a CMC. After copying CMCs from prod,
// orphaned mappings (null categoryMarketCategory) would crash on category select.
const categoryWithValidMappings = (category: CategoryListingConfiguration['category']) => {
  if (!category) return category

  return {
    ...category,
    categoryMappings: (category.categoryMappings || []).filter(cm => cm?.categoryMarketCategory?.id)
  }
}

const buildCategoryDefaults = (resData: CategoryListingConfiguration, carrierId?: number): Partial<EbayForm> => {
  const category = categoryWithValidMappings(resData.category)
  const ebayCmc = findActiveCmc(category?.categoryMappings, EBAY_CHANNEL_ID)
  const resolver = new ListingDefaultResolver(resData.marketSettings, category, carrierId)
  // ListingDefaultResolver.getBlankEbayListing expects a configuration object (or at least {}).
  // Passing null throws when it reads configuration.marketAccountId / policies.
  const defaults = resolver.getBlankEbayListing({ marketId: EBAY_CHANNEL_ID })

  return {
    ...defaults,
    categoryMarketCategoryId: ebayCmc?.id ?? defaults.categoryMarketCategoryId,
    categoryMarketCategory: ebayCmc ?? defaults.categoryMarketCategory,
    specifics: defaults.specifics || {},
    productListingDetails: defaults.productListingDetails || {}
  }
}

export const ebayFormReducer = (ebayForm: EbayForm, action: Action): EbayForm => {
  switch (action.type) {
    case 'clearForm': {
      return EMPTY_EBAY_FORM
    }
    case 'clearStrategyForm': {
      return {
        oldCategoryConfig: ebayForm.oldCategoryConfig,
        ...ebayForm.oldCategoryConfig,
        specifics: ebayForm.specifics || {},
        productListingDetails: ebayForm.productListingDetails || {},
        conditionDescriptors: ebayForm.conditionDescriptors
      }
    }
    case 'onValueChange': {
      const { newValues } = action

      return {
        ...ebayForm,
        ...newValues
      }
    }
    case 'applyAutofill': {
      // Merge template/requirement-derived values into the CURRENT specifics. Merging here (rather than in
      // the dispatching callback) avoids a stale closure: after switching categories the form is cleared,
      // and the reducer always sees that cleared state — so a previous category's specifics can't leak in.
      const { autofilled } = action

      return {
        ...ebayForm,
        specifics: {
          ...ebayForm.specifics,
          ...autofilled.specifics
        },
        ...(autofilled.condition ? { condition: autofilled.condition } : {})
      }
    }
    case 'onSpecificChange': {
      const { name, value } = action

      return {
        ...ebayForm,
        specifics: {
          ...ebayForm.specifics,
          [name]: value
        }
      }
    }
    case 'onProductDetailChange': {
      const { name, value } = action

      return {
        ...ebayForm,
        productListingDetails: {
          ...ebayForm.productListingDetails,
          [name]: value
        }
      }
    }
    case 'categoryConfigLoaded': {
      const { resData } = action
      const categoryConfig = buildCategoryDefaults(resData, action.carrierId)

      return {
        ...ebayForm,
        oldCategoryConfig: categoryConfig,
        ...categoryConfig
      }
    }
    case 'strategyConfigLoaded': {
      const { resData } = action
      const strategyConfiguration = resData.configuration
      const ebayConfiguration = strategyConfiguration.find(c => c.marketId === EBAY_CHANNEL_ID)

      if (!ebayConfiguration) {
        return ebayForm
      }

      const resolver = new ListingDefaultResolver(
        action.resData.marketSettings || action.marketSettings,
        categoryWithValidMappings(action.category),
        action.carrierId
      )
      const strategyDefaults = resolver.getBlankEbayListing(ebayConfiguration)

      return {
        ...ebayForm,
        ...strategyDefaults,
        // getBlankEbayListing's cmcMap ignores `active` and picks the first eBay mapping, so keep the active
        // CMC that categoryConfigLoaded already resolved via findActiveCmc (the category, not the strategy,
        // determines the eBay category).
        categoryMarketCategoryId: ebayForm.categoryMarketCategoryId ?? strategyDefaults.categoryMarketCategoryId,
        categoryMarketCategory: ebayForm.categoryMarketCategory ?? strategyDefaults.categoryMarketCategory,
        specifics: ebayForm.specifics || {},
        productListingDetails: ebayForm.productListingDetails || {},
        conditionDescriptors: ebayForm.conditionDescriptors
      }
    }
    default: {
      throw Error(`Unknown action: ${action.type}`)
    }
  }
}
